import React from 'react';
import moment from 'moment';
import Grid from "../grid/grid";
import {dateToString} from "../../utils/date";
import {prettyAmount} from "../../utils/amount";
import './reports.css'
import {capitalizeAllWords} from "../../utils/text";
import Menu from "./menu";
import {StoreContext} from "../../store";
import {resolveTopPredictionDisplayDate} from "./topPredictionsDate";

const formatTopPredictionPrice = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) {
    return prettyAmount(value, 2);
  }
  if (Math.abs(amount) < 10) {
    return prettyAmount(value, 4);
  }
  if (Math.abs(amount) < 100) {
    return prettyAmount(value, 3);
  }
  return prettyAmount(value, 2);
};

const allowed = ['daily', 'two_day', 'weekly', 'two_week', 'monthly', 'two_month', 'three_month', 'six_month', 'yearly'];

const rankingModes = ['closest_realized', 'highest_predicted_return', 'strongest_predicted_move'];

const rankingLabels = {
  closest_realized: 'Closest Predictions',
  highest_predicted_return: 'Highest Return',
  strongest_predicted_move: 'Strongest Move',
};

const rankingTitleLabels = {
  closest_realized: 'Closest Predictions',
  highest_predicted_return: 'Highest Predicted Return',
  strongest_predicted_move: 'Strongest Predicted Move',
};

const metricHeaders = {
  closest_realized: {
    closeHeader: 'Close Price',
    metricHeader: '% Delta',
    metricTooltip: '|Prediction - Close Price| / Close Price x 100',
  },
  highest_predicted_return: {
    closeHeader: 'Release Close',
    metricHeader: 'Predicted Return %',
    metricTooltip: '(Prediction - Release Close) / Release Close x 100',
  },
  strongest_predicted_move: {
    closeHeader: 'Release Close',
    metricHeader: 'Predicted Move %',
    metricTooltip: '|Prediction - Release Close| / Release Close x 100',
  },
};

const buildColumns = (rankingMode) => {
  const metric = metricHeaders[rankingMode] || metricHeaders.closest_realized;
  return [
    'Ticker',
    {
      name: 'Close',
      header: metric.closeHeader,
      headerDOMProps: {
        title: metric.closeHeader,
      },
      render: ({value}) => formatTopPredictionPrice(value),
    },
    {name: 'Prediction', render: ({value}) => formatTopPredictionPrice(value)},
    {
      name: 'PercentDelta',
      header: metric.metricHeader,
      headerDOMProps: {
        title: metric.metricTooltip,
      },
      render: ({value}) => prettyAmount(value, 5),
    },
  ];
};

export const findBundledTopPrediction = (rankings, selectedTop50, horizon, rankingMode) => {
  const candidates = Array.isArray(rankings)
    ? rankings
    : (rankings && typeof rankings === 'object' ? Object.values(rankings) : []);
  const matches = (candidate) => (
    candidate?.meta?.horizon === horizon && candidate?.meta?.rankingMode === rankingMode
  );
  return candidates.find(matches) || (matches(selectedTop50) ? selectedTop50 : null);
};

export const mapBundledTopPredictionRows = (ranking) => (
  Array.isArray(ranking?.items)
    ? ranking.items.map((item, index) => ({
      Rank: item?.rank ?? index + 1,
      Ticker: item?.ticker,
      Close: item?.close,
      Prediction: item?.prediction,
      PercentDelta: item?.percentDelta,
      RankingValue: item?.rankingValue,
      CloseAtPredictionRelease: item?.closeAtPredictionRelease,
      RealizedClose: item?.realizedClose,
      PredictedReturnPct: item?.predictedReturnPct,
      PredictedMovePct: item?.predictedMovePct,
      RealizedPercentDelta: item?.realizedPercentDelta,
    })).filter((item) => item.Ticker)
    : []
);

const TopPredictions = ({
  bundledRankings,
  bundledTop50,
  deferInitialRequest = false,
  loading: bundledLoading = false,
}) => {
  const {store: {username, email} = {}} = React.useContext(StoreContext);
  const [date, setDate] = React.useState();
  const [value, setValue] = React.useState('daily');
  const [rankingMode, setRankingMode] = React.useState('closest_realized');
  const [values, setValues] = React.useState(allowed);
  const [continuity, setContinuity] = React.useState(null);
  const [hasExplicitSelection, setHasExplicitSelection] = React.useState(false);
  const columns = React.useMemo(() => buildColumns(rankingMode), [rankingMode]);
  const reportParams = React.useMemo(
    () => ({
      horizon: value,
      rankingMode,
      limit: 50,
    }),
    [rankingMode, value],
  );
  const bundledRanking = React.useMemo(
    () => findBundledTopPrediction(
      bundledRankings,
      bundledTop50,
      value,
      rankingMode,
    ),
    [bundledRankings, bundledTop50, rankingMode, value],
  );
  const bundledRows = React.useMemo(
    () => mapBundledTopPredictionRows(bundledRanking),
    [bundledRanking],
  );
  const bundledRankingUsable = Boolean(
    bundledRanking && bundledRanking?.status !== 'unavailable' && Array.isArray(bundledRanking?.items),
  );
  const shouldLoadRemote = (
    !bundledRankingUsable && (!deferInitialRequest || hasExplicitSelection)
  );

  const canViewContinuityWarning = React.useMemo(() => {
    const normalizedUser = String(username || "").trim().toLowerCase();
    const normalizedEmail = String(email || "").trim().toLowerCase();
    return normalizedUser === "nektarios" || normalizedEmail === "info@deeptrading.ai";
  }, [username, email]);
  const afterLoad = React.useCallback((data) => {
    setDate(resolveTopPredictionDisplayDate(data?.meta, value));
    setContinuity(data?.meta?.continuity || null);
    const loadedValues = Object
      .keys(data?.data || {})
      .map(item => item.replace('_prediction', ''))
      .filter(item => allowed.includes(item));
    if (loadedValues.length > 1) {
      setValues(loadedValues);
    }
  }, [value]);
  const prepareData = React.useCallback((data) => (data || {})[`${value}_prediction`], [value]);
  const handleHorizonChange = React.useCallback((nextValue) => {
    setHasExplicitSelection(true);
    setValue(nextValue);
  }, []);
  const handleRankingChange = React.useCallback((nextRankingMode) => {
    setHasExplicitSelection(true);
    setRankingMode(nextRankingMode);
  }, []);

  React.useEffect(() => {
    if (!bundledRanking) return;
    setDate(resolveTopPredictionDisplayDate(bundledRanking.meta, value));
    setContinuity(bundledRanking?.meta?.continuity || null);
  }, [bundledRanking, value]);

  return (
    <>
      <Menu
        value={value}
        onChange={handleHorizonChange}
        items={values}
      />
      <Menu
        value={rankingMode}
        onChange={handleRankingChange}
        items={rankingModes}
        labels={rankingLabels}
      />
      <div className="report-main">
        <h6 className="report-name">
          Top 50 {capitalizeAllWords(value)} {rankingTitleLabels[rankingMode] || rankingTitleLabels.closest_realized}
          {date && (
            <>
              &nbsp;for {dateToString(moment.utc(date), 'MMMM Do, YYYY')}
            </>
          )}
        </h6>
        {canViewContinuityWarning && continuity?.finalCheckFailed && (
          <div className="report-warning">
            {continuity?.message || "Prediction continuity test failed at 6:45 PM ET."}
          </div>
        )}
        <Grid
          key={`top-predictions-${value}-${rankingMode}`}
          idProperty="Ticker"
          columns={columns}
          report={shouldLoadRemote ? "topPredictions" : undefined}
          reportParams={reportParams}
          dataSource={bundledRows}
          loading={bundledLoading && !bundledRanking}
          className="singleStockChartTable report-size"
          rowHeight={35}
          autoHeight
          sortable
          afterLoad={afterLoad}
          prepareData={shouldLoadRemote ? prepareData : undefined}
        />
      </div>
    </>
  )
}

export default React.memo(TopPredictions);
